// Shared GLSL for the environment shaders (§12.5): hashes, value noise, fbm, Voronoi and polar
// helpers. Every chunk is plain GLSL ES 3 text, prepended to the shaders that use it.

export const GLSL_NOISE = /* glsl */ `
float envHash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
vec2 envHash22(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.xx + p3.yz) * p3.zy);
}
float envNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = envHash12(i);
  float b = envHash12(i + vec2(1.0, 0.0));
  float c = envHash12(i + vec2(0.0, 1.0));
  float d = envHash12(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}
float envFbm(vec2 p, int octaves) {
  float v = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 6; i++) {
    if (i >= octaves) break;
    v += amp * envNoise(p);
    p = mat2(1.6, 1.2, -1.2, 1.6) * p + 17.1;
    amp *= 0.5;
  }
  return v;
}
// Voronoi: x = distance to the nearest feature point, y = distance to the cell border.
vec2 envVoronoi(vec2 p) {
  vec2 n = floor(p);
  vec2 f = fract(p);
  vec2 mg = vec2(0.0);
  vec2 mr = vec2(0.0);
  float md = 8.0;
  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
    vec2 g = vec2(float(i), float(j));
    vec2 o = envHash22(n + g);
    vec2 r = g + o - f;
    float d = dot(r, r);
    if (d < md) { md = d; mr = r; mg = g; }
  }
  float border = 8.0;
  for (int j = -2; j <= 2; j++) for (int i = -2; i <= 2; i++) {
    vec2 g = mg + vec2(float(i), float(j));
    vec2 o = envHash22(n + g);
    vec2 r = g + o - f;
    if (dot(mr - r, mr - r) > 0.00001) border = min(border, dot(0.5 * (mr + r), normalize(r - mr)));
  }
  return vec2(sqrt(md), border);
}
// Nearest feature only (3×3, cheaper than envVoronoi): xy = offset from the feature point to p,
// z = distance, w = a random id of the nearest cell (0..1).
vec4 envCells(vec2 p) {
  vec2 n = floor(p);
  vec2 f = fract(p);
  vec4 best = vec4(0.0, 0.0, 8.0, 0.0);
  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
    vec2 g = vec2(float(i), float(j));
    vec2 o = envHash22(n + g);
    vec2 r = f - g - o;
    float d = dot(r, r);
    if (d < best.z) best = vec4(r, d, envHash12(n + g + 7.3));
  }
  best.z = sqrt(best.z);
  return best;
}
// Distance to the nearest edge of a pointy-top hex grid of cell size s (centers on a lattice).
float envHexDist(vec2 p, float s) {
  vec2 r = vec2(1.0, 1.7320508);
  vec2 h = r * 0.5;
  p /= s;
  vec2 a = mod(p, r) - h;
  vec2 b = mod(p - h, r) - h;
  vec2 g = dot(a, a) < dot(b, b) ? a : b;
  return length(g) * s;
}
float envAa(float d, float w) {
  return clamp(0.5 - d / max(w, 1e-4), 0.0, 1.0);
}
`;
